import { jsx } from 'react/jsx-runtime';
import { createConsentService } from '@iwt-core-web/consent';
import { use, useState, useEffect, useCallback, useMemo, createContext } from 'react';

const ConsentServiceContext = /*#__PURE__*/ createContext(null);
const useConsentService = ()=>{
    const context = use(ConsentServiceContext);
    if (!context) {
        throw new Error("The consent service provider is not set. Make sure to add a parent `ConsentServiceProvider` in your React tree.");
    }
    return context;
};
const ConsentServiceProvider = ({ id, children, enableTCF, gimiiRaiserId, onBannerInteraction, onError })=>{
    const [isConsentGranted, setIsConsentGranted] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [consentItems, setConsentItems] = useState({});
    const [service, setService] = useState(null);
    useEffect(()=>{
        const initializeConsentService = async ()=>{
            try {
                const consentService = await createConsentService(id, {
                    enableTCF,
                    gimiiRaiserId,
                    onBannerInteraction (input) {
                        const updateConsentItems = async ()=>{
                            setConsentItems(await input.service.getConsentItems());
                        };
                        setIsConsentGranted(true);
                        void updateConsentItems();
                        if (typeof onBannerInteraction === "function") {
                            onBannerInteraction(input);
                        }
                    },
                    onInitialization () {
                        setIsInitialized(true);
                    },
                    onError (error) {
                        if (typeof onError === "function") {
                            onError(error);
                        }
                    }
                });
                setService(consentService);
                setIsConsentGranted(await consentService.getIsConsentGranted());
                setConsentItems(await consentService.getConsentItems());
            } catch (error) {
                if (typeof onError === "function") {
                    onError(error instanceof Error ? error : new Error(String(error)));
                }
            }
        };
        void initializeConsentService();
    }, [
        enableTCF,
        gimiiRaiserId,
        id,
        onBannerInteraction,
        onError
    ]);
    const showBanner = useCallback(async ()=>{
        if (!service) return;
        return service.showBanner();
    }, [
        service
    ]);
    const value = useMemo(()=>{
        return {
            consentItems,
            isConsentGranted,
            isInitialized,
            showBanner
        };
    }, [
        consentItems,
        isConsentGranted,
        isInitialized,
        showBanner
    ]);
    return /*#__PURE__*/ jsx(ConsentServiceContext, {
        value: value,
        children: children
    });
};

export { ConsentServiceProvider, useConsentService };
